import { findById } from "./dash.js";
import { get, isEmpty, set } from "es-toolkit/compat";
/**
 * Get visible layers from layer list
 */
export function getVisibleLayers(layers) {
    const visibleLayers = [];
    for (const layer of layers) {
        const visibility = get(layer, "layout.visibility", "visible");
        if (visibility === "visible") {
            visibleLayers.push(layer.id);
        }
    }
    return visibleLayers;
}
/**
 * Extract layer groups from layer tree config
 */
export function getLayerGroups(groups) {
    return groups.reduce((acc, group) => {
        if ("children" in group) {
            return acc.concat([
                {
                    label: group.label,
                    value: group.value,
                    icon: group.icon,
                },
                ...getLayerGroups(group.children),
            ]);
        }
        return acc;
    }, []);
}
/**
 * Create layer tree from config
 */
export function createLayerTree(config, layers, parent) {
    return config.map((item) => {
        if ("children" in item) {
            const { value, label, icon, children } = item;
            const childGroups = getLayerGroups(children);
            if (!Array.isArray(children)) {
                throw new Error(`Children are not array for layer group ${value}`);
            }
            if (isEmpty(label)) {
                throw new Error(`Label is missing for layer group ${value}`);
            }
            const nodeProps = {
                icon,
                parent,
                childGroups,
            };
            return {
                value,
                label,
                nodeProps,
                children: createLayerTree(children, layers, value),
            };
        }
        else {
            const { value } = item;
            const layer = findById(layers, value);
            if (!layer) {
                throw new Error(`Layer with id ${value} not found for layer group ${parent}`);
            }
            if (isEmpty(value)) {
                throw new Error(`Layer value is missing for layer group ${parent}`);
            }
            const nodeProps = {
                parent,
                layer,
            };
            return {
                label: value,
                value: value,
                nodeProps,
            };
        }
    });
}
/**
 * Add new group to the layer tree
 * @note mutable change to groups
 */
export function addLayerGroup(layerTree, newGroup) {
    if (!newGroup.parent) {
        layerTree.push({
            value: newGroup.value,
            label: newGroup.label,
            icon: newGroup.icon,
            children: newGroup.children,
        });
        return;
    }
    for (const group of layerTree) {
        if ("children" in group) {
            if (group.value === newGroup.parent) {
                group.children.push({
                    value: newGroup.value,
                    label: newGroup.label,
                    icon: newGroup.icon,
                    children: newGroup.children,
                });
                return;
            }
            else {
                addLayerGroup(group.children, newGroup);
            }
        }
    }
}
/**
 * Add new layer to the layer tree
 * @note mutable change to groups
 */
export function addLayer(groups, newLayer) {
    if (!newLayer.parent) {
        groups.push({
            value: newLayer.value,
            label: newLayer.value,
        });
        return;
    }
    for (const group of groups) {
        if ("children" in group) {
            if (group.value === newLayer.parent) {
                group.children.push({
                    value: newLayer.value,
                    label: newLayer.value,
                });
                return;
            }
            else {
                addLayer(group.children, newLayer);
            }
        }
    }
}
/**
 * Remove item from tree and return it
 * @note mutable change to groups
 */
export function removeLayerItem(groups, value) {
    let removed = undefined;
    groups.forEach((group, index) => {
        if (group.value === value) {
            removed = groups.splice(index, 1).at(0);
        }
        if ("children" in group) {
            const item = removeLayerItem(group.children, value);
            if (item) {
                removed = item;
            }
        }
    });
    return removed;
}
/**
 * Move group or item to different parent group
 * @note mutable change to groups
 */
export function moveLayerItem(groups, item, from, to) {
    if (!from && !to) {
        return;
    }
    const removed = removeLayerItem(groups, item);
    if (!removed) {
        throw new Error(`Item with id ${item} not found for layer group ${from}`);
    }
    if ("children" in removed) {
        const { children, label, icon, value } = removed;
        addLayerGroup(groups, {
            label,
            icon,
            value,
            children,
            parent: to,
        });
        return;
    }
    if ("value" in removed) {
        const { value } = removed;
        addLayer(groups, {
            value,
            parent: to,
        });
        return;
    }
    throw new Error(`Item with id ${item} invalid for layer group ${from}`);
}
/**
 * Find layer group by value
 */
function findLayerGroupByValue(groups, groupValue) {
    for (const item of groups) {
        if ("children" in item) {
            const { value, children } = item;
            if (groupValue === value) {
                return item;
            }
            const found = findLayerGroupByValue(children, value);
            if (found) {
                return found;
            }
        }
    }
    return;
}
/**
 * Get child layers of group
 */
const getChildLayers = (layers, group) => {
    const childLayers = [];
    for (const child of group.children) {
        const { value } = child;
        if ("children" in child) {
            childLayers.push(...getChildLayers(layers, child));
        }
        else {
            const layer = findById(layers, value);
            if (layer) {
                childLayers.push(layer);
            }
        }
    }
    return childLayers;
};
/**
 * Change visibility of layer or group
 * @note mutable change to layers
 */
export function changeLayerItemVisibility(groups, layers, id, visibility) {
    const layer = findById(layers, id);
    if (layer) {
        set(layer, "layout.visibility", visibility);
        return;
    }
    const group = findLayerGroupByValue(groups, id);
    if (group) {
        getChildLayers(layers, group).forEach((groupLayer) => {
            set(groupLayer, "layout.visibility", visibility);
        });
        return;
    }
    throw new Error(`Layer or group with id ${id} not found`);
}
/**
 * Match layer id with search string
 */
export function matchLayer(layer, search) {
    return layer.id.toLowerCase().includes(search);
}
/**
 * Filter layer tree by search string
 */
export function filterLayerTree(tree, search = "") {
    const searchString = search.toLowerCase().trim();
    if (!searchString)
        return tree;
    const filteredTree = [];
    for (const item of tree) {
        if ("children" in item) {
            const { children } = item;
            const filteredChildren = filterLayerTree(children, searchString);
            if (filteredChildren.length) {
                filteredTree.push({
                    ...item,
                    children: filteredChildren,
                });
            }
        }
        else {
            const { nodeProps: { layer }, } = item;
            if (matchLayer(layer, searchString)) {
                filteredTree.push(item);
            }
        }
    }
    return filteredTree;
}
//# sourceMappingURL=layer.js.map